import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Accordion, Card, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { isEmpty } from 'lodash';
import CustomTooltip from '../../Components/Tooltip/CustomTooltip';
import Loader from '../../Components/Utility/Loader/Loader';
import { SelectBoxStyles, currencyCodes, convertSelectLabel } from '../../Components/helper';
import {
  getSubBrandAccess,
  saveSubBrandAccess,
  getSubBrandDetails,
  getBrandDetails,
  saveToSystemMaster,
  deleteFromSystemMasters,
  setActiveInactive,
} from './Store/SystemMasters.action';
import ConfirmationPopup from '../../Components/ModalPopup/ConfirmationPopup';
import { useComponent } from '../UserSetting/UsersLibrary/UserRights';

function CustomAttrSave(isLoading) {
  return isLoading ? <Spinner animation="border" /> : <i className="icon-Tick-1 f-24" />;
}
const EMPTY_BRAND = {
  name: null,
  isEmpty: false,
  isNew: true,
  isActive: true,
  _id: '',
  aliasName: '',
  currency: '',
};
function SubBrandMaster() {
  const dispatch = useDispatch();
  const reducer = useSelector((estate) => estate);
  const {
    SystemMastersDetails: { brandList, subBrandList, subBrandAccess },
    UserDetails: { userInfo },
    Users: { logInRights },
  } = reducer;
  const { t } = useTranslation('common');
  const loggedInUserId = userInfo.userId;
  const [buttonDownSubBrand, setButtonDownSubBrand] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [subBrandData, setSubBrandData] = useState([]);
  const [addingBrand, setAddingBrand] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [nonUniqueIds, setNonUniqueIds] = useState([]);
  const [statusChangeId, setStatusChangeId] = useState('');
  const [openPopup, setOpenPopup] = useState(false);
  const [showStatusConfirm, setShowStatusConfirm] = useState(false);
  const [pendingToggleValue, setPendingToggleValue] = useState(null);
  const [isLoadingConf, setIsLoadingConf] = useState(false);
  const [SelectSubBrandData, setSelectSubBrandData] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState('');
  const [isSubBrandVisible, setIsSubBrandVisible] = useState(false);
  const [userRights, setUserRights] = useState({});
  useEffect(() => {
    if (logInRights) {
      setUserRights(logInRights);
    }
  }, [logInRights]);

  useEffect(() => {
    return () => {
      setAddingBrand(false);
      setSubBrandData([]);
      setSelectSubBrandData(null);
      setNonUniqueIds([]);
    };
  }, []);

  const btnDisabled = useMemo(() => subBrandData?.some((item) => !item.name?.trim()), [subBrandData]);
  const isUnique = nonUniqueIds?.length === 0;

  const { CheckActionPermission } = useComponent();

  const MasterAddEditAccess = CheckActionPermission(userRights, 'masters', 'subBrand', 'addEdit');
  const MasterDeleteAccess = CheckActionPermission(userRights, 'masters', 'subBrand', 'delete');

  useEffect(() => {
    dispatch(getSubBrandAccess());
  }, [dispatch]);

  useEffect(() => {
    if (subBrandAccess !== undefined) {
      setIsSubBrandVisible(subBrandAccess);
      setPendingToggleValue(subBrandAccess);
    }
  }, [subBrandAccess]);

  const handleToggle = (e) => {
    const newValue = e.target.checked;
    setPendingToggleValue(newValue);
    setOpenPopup(true);
  };

  const toggleConfirmation = async () => {
    try {
      setOpenPopup(false);
      await dispatch(saveSubBrandAccess({ isSubBrand: pendingToggleValue }, t));
      setIsSubBrandVisible(pendingToggleValue);
    } catch (error) {
      console.error('Failed to update sub-brand access:', error);
      setPendingToggleValue(!pendingToggleValue);
    }
  };
  useEffect(() => {
    setSubBrandData(addingBrand ? [...subBrandList, EMPTY_BRAND] : subBrandList);
  }, [subBrandList, addingBrand]);

  useEffect(() => {
    if (subBrandList?.length > 0 && addingBrand) {
      const allItemsSaved = subBrandList.every((item) => item._id);
      if (allItemsSaved) {
        setAddingBrand(false);
      }
    }
  }, [subBrandList, addingBrand]);

  useEffect(() => {
    const duplicates = findDuplicateIndices(subBrandData);
    setNonUniqueIds(duplicates);
  }, [subBrandData]);

  useEffect(() => {
    if (SelectSubBrandData && Object.keys(SelectSubBrandData).length) subBrandOptions();
  }, [SelectSubBrandData]);

  const subBrandOptions = async () => {
    await dispatch(getSubBrandDetails({ brandId: SelectSubBrandData._id, loggedInUserId }));
  };

  const findDuplicateIndices = useCallback((brands) => {
    const nameMap = {};
    brands?.forEach((item, idx) => {
      const name = item?.name?.trim();
      if (!name) return;
      nameMap[name] = nameMap[name] || [];
      nameMap[name].push(idx);
    });
    return Object.values(nameMap).flatMap((indices) => (indices?.length > 1 ? indices : []));
  }, []);

  const handleToggleAccordion = async () => {
    setButtonDownSubBrand(!buttonDownSubBrand);
    setIsOpen(buttonDownSubBrand);
    if (buttonDownSubBrand) {
      const brandParams = {
        loggedInUserId,
      };
      setIsLoading(true);
      await dispatch(getBrandDetails(brandParams));
      setSubBrandData([]);
      setAddingBrand(false);
      setIsLoading(false);
    } else {
      setAddingBrand(false);
      setSubBrandData([]);
      setSelectSubBrandData(null);
    }
  };

  const handleInputChange = (e, index) => {
    const { name, value } = e.target;
    const updatedData = [...subBrandData];

    if (name === 'aliasName') {
      updatedData[index] = {
        ...updatedData[index],
        aliasName: value,
      };
    } else {
      updatedData[index] = {
        ...updatedData[index],
        name: value,
        isEmpty: !value.trim(),
      };
    }

    setSubBrandData(updatedData);
  };

  const handleCurrencyChange = useCallback((option, index) => {
    setSubBrandData((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], currency: option?.label || '' };
      return updated;
    });
  }, []);

  const handleAddSubBrand = () => {
    if (!isOpen) {
      setAddingBrand(true);
      handleToggleAccordion();
      if (isEmpty(subBrandList)) dispatch(getBrandDetails({ loggedInUserId }));
    } else {
      setSubBrandData((prev) => [...prev, EMPTY_BRAND]);
    }
  };

  const handleDelete = (id) => {
    if (typeof id === 'number' || id === '') {
      const updated = [...subBrandData];
      updated.splice(id, 1);
      setSubBrandData(updated);
    } else {
      setDeleteId(id);
      setShowDeleteConfirm(true);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    const payload = subBrandData.map((item) => ({
      ...item,
      name: item.name?.trim(),
      isEmpty: !item.name?.trim(),
      aliasName: item?.aliasName ? item?.aliasName?.trim() : '',
      currency: (item?.currency || '').trim(),
    }));
    if (payload.every((item) => item.name)) {
      const params = {
        brandId: SelectSubBrandData._id,
        subTypes: JSON.stringify(payload),
        loggedInUserId,
        showAlias: true,
        title: 'sub-brand',
      };

      await dispatch(saveToSystemMaster(params, t));
      setAddingBrand(false);
      await subBrandOptions();
    } else {
      setSubBrandData(payload);
    }
    setIsSaving(false);
  };

  const handleSwitch = (id) => {
    setStatusChangeId(id);
    setShowStatusConfirm(true);
  };

  const confirmDelete = async () => {
    setIsLoadingConf(true);
    await dispatch(
      deleteFromSystemMasters(
        {
          id: deleteId,
          brandId: SelectSubBrandData._id,
          title: 'sub-brand',
        },
        t,
      ),
    );
    setIsLoadingConf(false);
    setShowDeleteConfirm(false);
    await subBrandOptions();
  };

  const confirmStatusChange = async () => {
    setIsLoadingConf(true);
    const item = subBrandData.find((ele) => ele._id === statusChangeId);
    if (item) {
      const payload = {
        id: item._id,
        brandId: item.brandId,
        isActive: !item.isActive,
        loggedInUserId,
        title: 'sub-brand',
      };
      await dispatch(setActiveInactive(payload, t));
    }
    setIsLoadingConf(false);
    setShowStatusConfirm(false);
    await subBrandOptions();
  };

  const BrandOptions = useMemo(() => brandList.filter((item) => item), [brandList]);
  const currencyOptions = useMemo(() => currencyCodes?.map((k) => convertSelectLabel(k, true)), []);
  return (
    <>
      <Accordion className="mb-4 master-accordian custom-attribute-master">
        <Card className="card">
          <Card.Header>
            <div className="title-container">
              <div className="custom-control pl-0 d-flex design-switch pt-0">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="subBrand"
                  name="subBrand"
                  checked={isSubBrandVisible}
                  onChange={handleToggle}
                  disabled={!(userInfo?.role === 'admin' || userInfo?.role === 'Admin')}
                />
                <label className="design-title mb-0" htmlFor="subBrand">
                  <span className="name-text text-black text-bold f-18">{t('SUB_BRAND.TITTLE')}</span>
                </label>
              </div>
            </div>
            <div className="action-title-btns">
              <CustomTooltip tooltipText={buttonDownSubBrand ? t('CUSTOM_ATTRIBUTES.OPEN') : t('CUSTOM_ATTRIBUTES.CLOSE')}>
                <button onClick={handleToggleAccordion} className="btn d-inline-flex p-0" type="button" aria-label="toggle-accordion">
                  <i className={`icon-new-down-arrow-icon f-24${!buttonDownSubBrand ? ' icon-inverse' : ''}`} />
                </button>
              </CustomTooltip>
            </div>
          </Card.Header>

          {isOpen && (
            <Card.Body>
              {isLoading ? (
                <Loader />
              ) : (
                <>
                  <div className="select-container">
                    <Select
                      options={BrandOptions}
                      placeholder={t('SUB_BRAND.SELECT_BRAND')}
                      getOptionLabel={(o) => o.name}
                      getOptionValue={(o) => o._id}
                      name="subBrand"
                      value={SelectSubBrandData}
                      styles={SelectBoxStyles}
                      onChange={setSelectSubBrandData}
                      className="common-select"
                      classNamePrefix="common-select"
                      isClearable
                    />
                  </div>

                  <div className="common-scrollbar flex-fill mt-3">
                    <ul>
                      {subBrandData?.map((item, index) => (
                        <li key={index}>
                          <input
                            name="aliasName"
                            value={item?.aliasName}
                            maxLength={10}
                            onChange={(e) => handleInputChange(e, index)}
                            className="w-50px mr-2"
                          />
                          <div className="input-container">
                            <input
                              type="text"
                              placeholder={t('SUB_BRAND.ADD_SUB_BRAND')}
                              value={item.name}
                              onChange={(e) => handleInputChange(e, index)}
                              maxLength={30}
                            />
                            {nonUniqueIds.includes(index) && <label className="text-danger p-1">{t('SYSTEM_MASTER_TABLE.THIS_ENTRY_ALREADY_EXISTS')}</label>}
                            {item.isEmpty && <label className="text-danger p-1">{t('SYSTEM_MASTER_TABLE.SUBBRAND_ENTRY_REQUIRED')}</label>}
                          </div>
                          <div className="common-react-select mr-2" style={{ minWidth: 140 }}>
                            <Select
                              options={currencyOptions}
                              getOptionLabel={({ label }) => `${label}`}
                              getOptionValue={({ label }) => `${label}`}
                              styles={SelectBoxStyles}
                              className="common-select"
                              classNamePrefix="common-select"
                              menuPosition="fixed"
                              isClearable
                              placeholder={t('account:USER_SETTING.ACCOUNT_DETAIL.CURRENCY')}
                              value={currencyOptions?.find((o) => o.label === item.currency) || null}
                              onChange={(option) => handleCurrencyChange(option, index)}
                              isDisabled={!MasterAddEditAccess}
                            />
                          </div>
                          {item._id && MasterAddEditAccess && (
                            <CustomTooltip tooltipText={t('SYSTEM_MASTER_TABLE.ACTIVE/INACTIVE')}>
                              <button
                                type="button"
                                className="btn p-0 custom-action mr-0"
                                onClick={() => handleSwitch(item._id)}
                                id={item?._id}
                                aria-label={item?.isActive ? 'Active' : 'Inactive'}
                              >
                                <span className={item.isActive ? 'icon-Eye-Open' : 'icon-Eye-Closed'} />
                              </button>
                            </CustomTooltip>
                          )}
                          <div className="input-action d-flex">
                            {MasterDeleteAccess && (
                              <CustomTooltip tooltipText={t('SYSTEM_MASTER_TABLE.DELETE')}>
                                <button
                                  type="button"
                                  className="btn p-0 custom-action mr-0"
                                  onClick={() => handleDelete(item?._id || index)}
                                  aria-label="delete"
                                >
                                  <span className="icon-Delete f-24" />
                                </button>
                              </CustomTooltip>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="license-btn-container">
                    {MasterAddEditAccess && (
                      <>
                        <CustomTooltip tooltipText={t('CUSTOM_ATTRIBUTES.ADD_TOOLTIP')}>
                          <button
                            type="button"
                            className="btn btn-outline ml-auto mr-0 mt-16 p-0"
                            onClick={handleAddSubBrand}
                            aria-label="Add"
                            disabled={!SelectSubBrandData || btnDisabled || !isUnique}
                          >
                            <span className="icon-Add text-grey  f-24" />
                          </button>
                        </CustomTooltip>

                        <div className="section-custom-save">
                          <button
                            aria-label="Save"
                            type="button"
                            className="btn p-0"
                            onClick={handleSave}
                            disabled={!subBrandData?.length || btnDisabled || !isUnique}
                          >
                            {CustomAttrSave(isSaving)}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
            </Card.Body>
          )}
        </Card>
      </Accordion>
      {openPopup && (
        <ConfirmationPopup
          onOpen={openPopup}
          toggle={() => setOpenPopup(false)}
          title={pendingToggleValue ? t('SYSTEM_MASTER_TABLE.CONFIRMATION_TITLE_SUBBRAND') : t('SYSTEM_MASTER_TABLE.CONFIMATION_SUBBRAND')}
          message={pendingToggleValue ? t('SYSTEM_MASTER_TABLE.CONFIRMATION_MESSAGE_TITLE_SUBBRAND') : t('SYSTEM_MASTER_TABLE.CONFIRMATION_MESSAGE_SUBBRAND')}
          onConfirm={toggleConfirmation}
        />
      )}
      <ConfirmationPopup
        onOpen={showDeleteConfirm}
        onConfirm={confirmDelete}
        title={t('LICENSE_MASTER.DELETE_CONFIRMATION.TITLE')}
        message={t('LICENSE_MASTER.DELETE_CONFIRMATION.MESSAGE')}
        toggle={() => setShowDeleteConfirm(false)}
        loading={isLoading}
      />
      <ConfirmationPopup
        onOpen={showStatusConfirm}
        onConfirm={confirmStatusChange}
        title={t('LICENSE_MASTER.ACTIVE_INACTIVE_CONFIRMATION.TITLE')}
        message={t('LICENSE_MASTER.ACTIVE_INACTIVE_CONFIRMATION.MESSAGE')}
        toggle={() => setShowStatusConfirm(false)}
        loading={isLoadingConf}
      />
    </>
  );
}

export default SubBrandMaster;
